import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  X,
  MapPin,
  Bed,
  Bath,
  Users,
  DollarSign,
  Save,
  Image,
  Building2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminProperties = () => {
  const { token } = useAuth();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProperty, setEditingProperty] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    booking_url: '',
    bedrooms: 1,
    bathrooms: 1,
    sleeps: 2,
    price_per_night: 100,
    featured: false,
    available: true,
    description: '',
    images: [],
    amenities: []
  });
  const [newImageUrl, setNewImageUrl] = useState('');
  const [newAmenity, setNewAmenity] = useState('');

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/properties`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setProperties(data);
      }
    } catch (error) {
      console.error('Error fetching properties:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setIsCreating(true);
    setEditingProperty(null);
    setFormData({
      name: '',
      location: '',
      booking_url: '',
      bedrooms: 1,
      bathrooms: 1,
      sleeps: 2,
      price_per_night: 100,
      featured: false,
      available: true,
      description: '',
      images: [],
      amenities: []
    });
  };

  const handleEdit = (property) => {
    setEditingProperty(property);
    setIsCreating(false);
    setFormData({
      name: property.name || '',
      location: property.location || '',
      booking_url: property.booking_url || '',
      bedrooms: property.bedrooms || 1,
      bathrooms: property.bathrooms || 1,
      sleeps: property.sleeps || 2,
      price_per_night: property.price_per_night || 100,
      featured: property.featured || false,
      available: property.available !== false,
      description: property.description || '',
      images: property.images || [],
      amenities: property.amenities || []
    });
  };

  const handleSave = async () => {
    try {
      const url = editingProperty 
        ? `${API_URL}/api/admin/properties/${editingProperty.id}`
        : `${API_URL}/api/admin/properties`;
      
      const response = await fetch(url, {
        method: editingProperty ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        fetchProperties();
        setEditingProperty(null);
        setIsCreating(false);
      }
    } catch (error) {
      console.error('Error saving property:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this property?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/properties/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchProperties();
      }
    } catch (error) {
      console.error('Error deleting property:', error);
    }
  };

  const addImage = () => {
    if (newImageUrl.trim()) {
      setFormData({ ...formData, images: [...formData.images, newImageUrl.trim()] });
      setNewImageUrl('');
    }
  };

  const removeImage = (index) => {
    setFormData({ ...formData, images: formData.images.filter((_, i) => i !== index) });
  };

  const addAmenity = () => {
    if (newAmenity.trim() && !formData.amenities.includes(newAmenity.trim())) {
      setFormData({ ...formData, amenities: [...formData.amenities, newAmenity.trim()] });
      setNewAmenity('');
    }
  };

  const removeAmenity = (amenity) => {
    setFormData({ ...formData, amenities: formData.amenities.filter(a => a !== amenity) });
  };

  const handleCancel = () => {
    setEditingProperty(null);
    setIsCreating(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  // Edit/Create Form
  if (editingProperty || isCreating) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>{isCreating ? 'Add New Property' : 'Edit Property'}</CardTitle>
          <Button variant="ghost" size="sm" onClick={handleCancel}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Property Name</Label>
              <Input 
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Capitol Hill 1"
              />
            </div>
            <div className="space-y-2">
              <Label>Location</Label>
              <Input 
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="123 Main St, Washington DC"
              />
            </div>
            <div className="space-y-2">
              <Label>Booking URL</Label>
              <Input 
                value={formData.booking_url}
                onChange={(e) => setFormData({ ...formData, booking_url: e.target.value })}
                placeholder="https://book.example.com/property"
              />
            </div>
            <div className="space-y-2">
              <Label>Price Per Night ($)</Label>
              <Input 
                type="number"
                value={formData.price_per_night}
                onChange={(e) => setFormData({ ...formData, price_per_night: parseInt(e.target.value) || 0 })}
              />
            </div>
          </div>

          {/* Room Details */}
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Bedrooms</Label>
              <Input 
                type="number"
                value={formData.bedrooms}
                onChange={(e) => setFormData({ ...formData, bedrooms: parseInt(e.target.value) || 1 })}
              />
            </div>
            <div className="space-y-2">
              <Label>Bathrooms</Label>
              <Input 
                type="number"
                step="0.5"
                value={formData.bathrooms}
                onChange={(e) => setFormData({ ...formData, bathrooms: parseFloat(e.target.value) || 1 })}
              />
            </div>
            <div className="space-y-2">
              <Label>Sleeps</Label>
              <Input 
                type="number"
                value={formData.sleeps}
                onChange={(e) => setFormData({ ...formData, sleeps: parseInt(e.target.value) || 2 })}
              />
            </div>
          </div>

          {/* Toggles */}
          <div className="flex gap-6">
            <div className="flex items-center gap-2">
              <Switch 
                checked={formData.featured}
                onCheckedChange={(checked) => setFormData({ ...formData, featured: checked })}
              />
              <Label>Featured Property</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch 
                checked={formData.available}
                onCheckedChange={(checked) => setFormData({ ...formData, available: checked })}
              />
              <Label>Available</Label>
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea 
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={4}
              placeholder="Property description..."
            />
          </div>

          {/* Images */}
          <div className="space-y-2">
            <Label>Images</Label>
            <div className="flex gap-2">
              <Input 
                value={newImageUrl}
                onChange={(e) => setNewImageUrl(e.target.value)}
                placeholder="https://example.com/image.jpg"
              />
              <Button onClick={addImage} variant="outline">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {formData.images.map((img, idx) => (
                <div key={idx} className="relative group">
                  <img src={img} alt="" className="w-20 h-20 object-cover rounded" />
                  <button 
                    onClick={() => removeImage(idx)}
                    className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Amenities */}
          <div className="space-y-2">
            <Label>Amenities</Label>
            <div className="flex gap-2">
              <Input 
                value={newAmenity}
                onChange={(e) => setNewAmenity(e.target.value)}
                placeholder="WiFi, Air Conditioning, etc."
                onKeyPress={(e) => e.key === 'Enter' && addAmenity()}
              />
              <Button onClick={addAmenity} variant="outline">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {formData.amenities.map((amenity, idx) => (
                <Badge key={idx} variant="secondary" className="flex items-center gap-1">
                  {amenity}
                  <button onClick={() => removeAmenity(amenity)}>
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-4 border-t">
            <Button onClick={handleSave} className="bg-amber-500 hover:bg-amber-600 text-slate-900">
              <Save className="h-4 w-4 mr-2" />
              Save Property
            </Button>
            <Button variant="outline" onClick={handleCancel}>Cancel</Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Properties List
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-slate-900">Properties ({properties.length})</h2>
        <Button onClick={handleCreate} className="bg-amber-500 hover:bg-amber-600 text-slate-900">
          <Plus className="h-4 w-4 mr-2" />
          Add Property
        </Button>
      </div>

      <div className="grid gap-4">
        {properties.map((property) => (
          <Card key={property.id} className="overflow-hidden">
            <CardContent className="p-4">
              <div className="flex gap-4">
                {property.images?.[0] && (
                  <img 
                    src={property.images[0]} 
                    alt={property.name}
                    className="w-32 h-24 object-cover rounded-lg flex-shrink-0"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold text-slate-900">{property.name}</h3>
                      <p className="text-sm text-slate-500 flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {property.location}
                      </p>
                    </div>
                    <div className="flex gap-1">
                      {property.featured && (
                        <Badge className="bg-amber-100 text-amber-800">Featured</Badge>
                      )}
                      {!property.available && (
                        <Badge variant="secondary">Unavailable</Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4 mt-2 text-sm text-slate-600">
                    <span className="flex items-center gap-1">
                      <Bed className="h-4 w-4" /> {property.bedrooms}
                    </span>
                    <span className="flex items-center gap-1">
                      <Bath className="h-4 w-4" /> {property.bathrooms}
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="h-4 w-4" /> {property.sleeps}
                    </span>
                    <span className="flex items-center gap-1 font-semibold text-slate-900">
                      <DollarSign className="h-4 w-4" /> {property.price_per_night}/night
                    </span>
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <Button variant="outline" size="sm" onClick={() => handleEdit(property)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleDelete(property.id)} className="text-red-600 hover:bg-red-50">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {properties.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center">
              <Building2 className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No properties yet. Add your first property!</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AdminProperties;
